import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { DayPicker } from '@/components/shared/date/DayPicker';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { constructNameEN } from '@/employee/employee-name-helper';
import { dateToApiFormat, secondsToTimeString } from '@/helpers/dateHelper';
import { useGetStaffForDate } from '@/services/staff/useGetStaffForDate';
import { FunctionComponent, Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ViewRosterHeader } from './ViewRosterHeader';

const ViewRoster: FunctionComponent = () => {
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();

  if (!date) {
    throw new Error('Date parameter is required');
  }

  const dateObj = useMemo(() => new Date(date), [date]);
  const [selectedDate, setSelectedDate] = useState<Date>(dateObj);

  useEffect(() => {
    setSelectedDate(dateObj);
  }, [dateObj]);

  const onChangeDate = (newDate: Date) => {
    setSelectedDate(newDate);
    navigate(`/ess/my-calendar/${dateToApiFormat(newDate)}/view-roster`, { replace: true });
  };

  const { coworkersOnShift, otherStaff } = useGetStaffForDate(selectedDate);

  const sortedShifts = useMemo(
    () =>
      coworkersOnShift
        ?.flatMap(day => day.shifts?.map(shift => ({ ...shift, identityId: day.identityId })) ?? [])
        .sort((a, b) => a.start - b.start) ?? [],
    [coworkersOnShift]
  );

  return (
    <PageContainer customHeader={<ViewRosterHeader />}>
      <div className="bg-white border-b-2">
        <PageSizer mode={SizerMode.PAGE}>
          <DayPicker value={selectedDate} onChange={onChangeDate} />
        </PageSizer>
      </div>
      <Suspense fallback={<TableLoader />}>
        <div className="overflow-y-hidden flex flex-col bg-white">
          <PageSizer mode={SizerMode.PAGE} className="flex-1 overflow-auto">
            <ul className="divide-y">
              {sortedShifts.map(shift => {
                const { identityId } = shift;
                const employee = otherStaff?.find(s => s.identityId === identityId);

                const firstName = employee?.firstName ?? '';
                const lastName = employee?.lastName ?? '';

                const initials = `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
                const fullName = constructNameEN(firstName, lastName);
                const startTime = secondsToTimeString(shift.start);
                const endTime = secondsToTimeString(shift.end);

                return (
                  <li key={`${identityId}-shift-${shift.start}-${shift.end}`} className="flex items-center gap-3 py-3">
                    <div className="w-9 h-9 rounded-full bg-gray-400 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-semibold">{initials}</span>
                    </div>
                    <span className="flex-1 text-sm text-gray-900">{fullName}</span>
                    <span className="text-sm text-gray-500">
                      {startTime} - {endTime}
                    </span>
                  </li>
                );
              })}
            </ul>
          </PageSizer>
        </div>
      </Suspense>
    </PageContainer>
  );
};

export default ViewRoster;
